import {checkRequiredFields} from '../../features/additionalFields';
import {updateCustomerDataInBrowser} from '../../features/customer';
import {DefaultCart} from '../../reducers/cartReducer';
import {Environment, Feature, startModalProcessing, stopModalLoading} from '../../reducers/environmentReducer';
import {MerchantConfiguration} from '../../reducers/merchantConfigurationReducer';
import {PaymentConfiguration, registerGatewayBatch} from '../../reducers/paymentConfigurationReducer';
import {PeachPayCustomer, updateCustomerPreferredGateway} from '../../reducers/peachPayCustomerReducer';
import {PeachPayOrder} from '../../reducers/peachPayOrderReducer';
import {store} from '../../store';
import {cartItemQuantity} from '../../util/cart';
import {getLocaleText} from '../../util/translation';
import {clearLocalSession} from '../session';
import {setupSquareButtonRendering} from './button';
import {type PaymentRequestOptions, type LineItem, type TokenResult, type StoreVerifyBuyerDetails, type ChargeVerifyBuyerDetails, type BillingContact, type Payments} from '@square/web-payments-sdk-types';
import {loadScript, $qsAll} from '../../../../@shared/ts/dom';
import {FeatureFlag} from '../../../../@type/features';
import {type SquareGateway, type ProcessedTokenResult} from '../../models/square';
import {initSquareACHDebitMethod} from './achBank';
import {initSquareAfterpayMethod} from './afterpay';
import {initSquareCashAppMethod} from './cashapp';
import {initSquareApplePayMethod} from './applepay';
import {initSquareCardMethod} from './card';
import {initSquareGooglePayMethod} from './googlepay';
import {displayPaymentErrorMessage, type OrderService} from '../order';
import {type GatewayConfiguration} from '../../models/GatewayConfiguration';

export default async function initSquarePaymentIntegration(orderService: OrderService) {
	const applicationId = Feature.metadata<string>(FeatureFlag.SQUARE_GATEWAYS, 'application_id');
	const locationId = Feature.metadata<string>(FeatureFlag.SQUARE_GATEWAYS, 'location_id');
	const scriptSrc = Feature.metadata<string>(FeatureFlag.SQUARE_GATEWAYS, 'script_src');

	if (!applicationId || !locationId || !scriptSrc) {
		return;
	}

	await loadScript(scriptSrc, 'Square');

	if (!window.Square) {
		return;
	}

	const payments = window.Square.payments(applicationId, locationId);
	const squareMethods = registerSquareGateways();

	setupSquareButtonRendering(squareMethods);

	const confirm = async (event: MouseEvent) => {
		if (!checkRequiredFields()) {
			return;
		}

		const $target = event.target as HTMLElement | null;
		const $button = $target?.closest<HTMLButtonElement>('button');
		if (!$button) {
			// cashapp places button in Shadow DOM element so this must be checked as well
			if (!$target?.closest('div')?.shadowRoot?.querySelector('button')) {
				return;
			}
		}

		const gateway = squareMethods[PaymentConfiguration.selectedGateway()];
		if (!gateway) {
			return;
		}

		await squarePaymentFlow(payments, gateway, orderService);
	};

	$qsAll('.square-btn', $button => {
		$button.addEventListener('click', confirm);
	});
	$qsAll('.square-custom-btn-container', $paymentRequest => {
		$paymentRequest.addEventListener('click', confirm);
	});

	let previousState = '';
	store.subscribe(() => {
		const data = squarePaymentRequestOptions();
		const currentState = JSON.stringify(data);

		if (previousState === currentState || Environment.modalUI.loadingMode() !== 'finished') {
			return;
		}

		previousState = currentState;

		for (const [_, gateway] of Object.entries(squareMethods)) {
			gateway?.reset?.();
			gateway.state = undefined;
			gateway.activate(payments, payments.paymentRequest(data));
		}
	});
}

function registerSquareGateways(): Record<string, SquareGateway> {
	const gatewayImplementations: Record<string, SquareGateway> = {};
	const gatewayConfigurations: Record<string, GatewayConfiguration> = {};

	if (Feature.enabled(FeatureFlag.SQUARE_CARD_GATEWAY)) {
		const method = initSquareCardMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.SQUARE_CARD_GATEWAY)) {
		const method = initSquareGooglePayMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.SQUARE_APPLEPAY_GATEWAY)) {
		const method = initSquareApplePayMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.SQUARE_ACH)) {
		const method = initSquareACHDebitMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.SQUARE_AFTERPAY)) {
		const method = initSquareAfterpayMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.SQUARE_CASHAPP)) {
		const method = initSquareCashAppMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	store.dispatch(registerGatewayBatch(gatewayConfigurations));

	return gatewayImplementations;
}

async function squarePaymentFlow(payments: Payments, gateway: SquareGateway, orderService: OrderService) {
	const isCashapp = gateway.config.gatewayId === 'peachpay_square_cashapp';

	if (!(gateway.config.gatewayId === 'peachpay_square_cashapp')) {
		// cashapp popup happens in the iframe HTML within a shadow-dom, not in a separate page, so detecting when it is closed is unreliable
		// however users can not interact with the express checkout when it is open so setting it to loading is unnecessary
		// and is difficult to stop loading reliably when it is closed.
		store.dispatch(startModalProcessing());
	}

	const [sourceId, verificationId, transactionUpdate] = await gateway.tokenize(payments);

	if (isCashapp) {
		// Once the cashapp modal has closed, we don't want to allow user to interact with the peachpay modal.
		store.dispatch(startModalProcessing());
	}

	const transaction = await orderService.startTransaction(gateway.config.gatewayId);
	if (!sourceId || !transaction) {
		displayPaymentErrorMessage(transactionUpdate.note);

		await transaction?.complete(transactionUpdate);
		store.dispatch(stopModalLoading());
		return;
	}

	const orderOptions: Record<string, string> = {
		peachpay_session_id: PeachPayOrder.sessionId(),
		peachpay_transaction_id: transaction.getId(),
		peachpay_square_source_id: sourceId,
	};

	if (verificationId) {
		orderOptions['peachpay_square_verification_id'] = verificationId;
	}

	const order = await orderService.placeOrder(transaction, orderOptions);
	if (order.result === 'failure') {
		await transaction?.complete();
		store.dispatch(stopModalLoading());
		return;
	}

	store.dispatch(updateCustomerPreferredGateway(PaymentConfiguration.selectedGateway()));

	updateCustomerDataInBrowser();
	clearLocalSession();

	const successURL = orderService.getOrderRedirect(order);
	if (window.top) {
		await transaction.complete();
		window.top.location = successURL;
	}
}

function squarePaymentRequestOptions(): PaymentRequestOptions {
	const paymentRequestOptions: PaymentRequestOptions = {
		countryCode: MerchantConfiguration.general.wcLocationInfoData()?.store_country ?? 'US',
		currencyCode: MerchantConfiguration.currency.code(),
		lineItems: squarePaymentRequestLineItems(),
		discounts: squarePaymentRequestDiscountOptions(),
		taxLineItems: [{
			label: getLocaleText('Tax'),
			amount: (DefaultCart.totalTax()).toFixed(2),
		}],
		total: {
			label: getLocaleText('Total'),
			amount: DefaultCart.total().toFixed(2),
		},
	};

	return paymentRequestOptions;
}

function squarePaymentRequestLineItems(): LineItem[] {
	const lineItems = new Array<LineItem>();

	for (const cartItem of DefaultCart.contents()) {
		lineItems.push({
			label: cartItem.name + ' × ' + cartItemQuantity(cartItem),
			amount: (Number.parseFloat(cartItem.display_price ?? cartItem.price) * cartItemQuantity(cartItem)).toFixed(2),
		});
	}

	return lineItems;
}

function squarePaymentRequestDiscountOptions(): LineItem[] {
	const discounts = new Array<LineItem>();

	for (const [coupon, amount] of Object.entries(DefaultCart.couponRecord())) {
		if (!amount) {
			continue;
		}

		discounts.push({
			id: coupon,
			amount: (amount).toFixed(2),
			label: coupon,
		});
	}

	return discounts;
}

export function createVerifyBuyerDetails(): StoreVerifyBuyerDetails | ChargeVerifyBuyerDetails {
	const billingContact: BillingContact = {
		addressLines: [PeachPayCustomer.billing.address1(), PeachPayCustomer.billing.address2()],
		city: PeachPayCustomer.billing.city(),
		countryCode: PeachPayCustomer.billing.country(),
		email: PeachPayCustomer.billing.email(),
		familyName: PeachPayCustomer.billing.lastName(),
		givenName: PeachPayCustomer.billing.firstName(),
		phone: PeachPayCustomer.billing.phone(),
		state: PeachPayCustomer.billing.state(),
		postalCode: PeachPayCustomer.billing.postal(),
	};

	if (DefaultCart.total() > 0) {
		return {
			intent: 'CHARGE',
			currencyCode: MerchantConfiguration.currency.code(),
			amount: String(DefaultCart.total()),
			billingContact,
		};
	}

	return {
		intent: 'STORE',
		billingContact,
	};
}

export function handleTokenResult(result: TokenResult): ProcessedTokenResult {
	const extractErrorMessage = (): string => {
		let message = '';
		if (result.errors && Array.isArray(result.errors)) {
			result.errors.forEach(error => {
				message += error.message + '. ';
			});
		}

		return message;
	};

	switch (result.status) {
		case 'OK':
			return [result.token ?? null, null, {}];
		case 'Cancel':
			return [null, null, {
				note: 'Customer canceled the Payment. ' + extractErrorMessage(),
				paymentStatus: 'canceled',
			}];
		case 'Abort':
			return [null, null, {
				note: 'Tokenizing the payment method was aborted. ' + extractErrorMessage(),
				paymentStatus: 'creationfailed',
			}];
		case 'Error':
			return [null, null, {
				note: 'Tokenizing the payment method encountered an error. ' + extractErrorMessage(),
				paymentStatus: 'creationfailed',
			}];
		case 'Invalid':
			return [null, null, {
				note: extractErrorMessage(),
				paymentStatus: 'creationfailed',
			}];
		case 'Unknown':
		default:
			return [null, null, {
				note: 'Tokenizing the payment method encountered an unknown status. ' + extractErrorMessage(),
				paymentStatus: 'creationfailed',
			}];
	}
}

export {
	squarePaymentRequestOptions,
};
